/**
 * DataLayer Core - Phase 5.1
 * Comprehensive dataLayer initialization and utilities
 * Part of Phase 5: DataLayer & AI Optimization
 */
(function() {
  'use strict';

  function debugLog() {
    if (window.location.hostname === 'localhost' || window.location.hostname.includes('myshopify.com')) {
      if (window.logger && typeof window.logger.debug === 'function') {
        window.logger.debug.apply(window.logger, arguments);
      }
    }
  }

  // Ensure dataLayer exists
  window.dataLayer = window.dataLayer || [];

  const DEFAULT_FEATURE_FLAGS_HOST = 'https://getsimilarproductshttp-309132447778.us-central1.run.app';

  function getFeatureFlagsBase() {
    const host = (window.__ENV__ && window.__ENV__.FEATURE_FLAGS_API_HOST) ||
                 DEFAULT_FEATURE_FLAGS_HOST ||
                 (window.__ENV__ && window.__ENV__.SHOPIFY_HOST) ||
                 window.location.origin;
    if (!host) {
      return '';
    }
    return host.replace(/\/+$/, '');
  }

  function buildFeatureFlagsUrl(path) {
    const base = getFeatureFlagsBase();
    if (!base || path.startsWith('http')) {
      return path;
    }
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${base}${normalizedPath}`;
  }

  /**
   * DataLayer Manager
   */
  const DataLayerManager = {
    /**
     * Initialize dataLayer with base configuration
     */
    init: function() {
      // Base configuration
      this.push({
        'event': 'dataLayer_initialized',
        'page_type': this.getPageType(),
        'currency': 'GBP',
        'country': 'GB',
        'language': 'en-GB',
        'timestamp': new Date().toISOString()
      });

      // Customer data (if available)
      if (window.Shopify && window.Shopify.customer) {
        this.pushCustomerData(window.Shopify.customer);
      }

      // Session data
      this.pushSessionData();
    },

    /**
     * Push data to dataLayer
     */
    push: function(data) {
      if (!window.dataLayer) {
        window.dataLayer = [];
      }
      window.dataLayer.push(data);
      
      // Debug logging (only in development)
      debugLog('[DataLayer]', data);
    },

    /**
     * Get current page type
     */
    getPageType: function() {
      const path = window.location.pathname;
      
      if (path === '/') return 'home';
      if (path.startsWith('/products/')) return 'product';
      if (path.startsWith('/collections/')) return 'collection';
      if (path.startsWith('/cart')) return 'cart';
      if (path.startsWith('/checkout')) return 'checkout';
      if (path.startsWith('/account')) return 'account';
      if (path.startsWith('/blogs/')) return 'blog';
      if (path.startsWith('/pages/')) return 'page';
      if (path.startsWith('/search')) return 'search';
      
      return 'other';
    },

    /**
     * Push customer data to dataLayer
     */
    pushCustomerData: function(customer) {
      if (!customer) return;

      this.push({
        'event': 'customer_data_loaded',
        'user_data': {
          'customer_id': customer.id || null,
          'customer_email': customer.email || null,
          'customer_type': customer.tags?.includes('business') ? 'business' : 'retail',
          'loyalty_tier': this.getLoyaltyTier(customer),
          'total_orders': customer.orders_count || 0,
          'total_spent': customer.total_spent || 0
        }
      });
    },

    /**
     * Get loyalty tier from customer
     */
    getLoyaltyTier: function(customer) {
      if (!customer) return 'none';
      
      const totalSpent = customer.total_spent || 0;
      if (totalSpent >= 10000) return 'platinum';
      if (totalSpent >= 5000) return 'gold';
      if (totalSpent >= 1000) return 'silver';
      return 'bronze';
    },

    /**
     * Push session data
     */
    pushSessionData: function() {
      const sessionId = this.getSessionId();
      const sessionStart = sessionStorage.getItem('session_start') || new Date().toISOString();
      
      if (!sessionStorage.getItem('session_start')) {
        sessionStorage.setItem('session_start', sessionStart);
      }

      this.push({
        'event': 'session_data',
        'session_id': sessionId,
        'session_start': sessionStart,
        'page_views': this.incrementPageViews()
      });
    },

    /**
     * Get or create session ID
     */
    getSessionId: function() {
      let sessionId = sessionStorage.getItem('session_id');
      if (!sessionId) {
        sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        sessionStorage.setItem('session_id', sessionId);
      }
      return sessionId;
    },

    /**
     * Increment and return page views
     */
    incrementPageViews: function() {
      const views = parseInt(sessionStorage.getItem('page_views') || '0', 10) + 1;
      sessionStorage.setItem('page_views', views.toString());
      return views;
    },

    /**
     * Format product data for dataLayer
     */
    formatProduct: function(product) {
      if (!product) return null;

      return {
        'item_id': product.id || product.variant_id || null,
        'item_name': product.title || product.name || null,
        'item_category': product.product_type || null,
        'item_category2': product.vendor || null,
        'item_brand': product.vendor || null,
        'item_variant': product.variant_title || null,
        'price': parseFloat(product.price || product.price_min || 0) / 100,
        'quantity': parseInt(product.quantity || 1, 10),
        'currency': 'GBP',
        'sku': product.sku || null
      };
    },

    /**
     * Format cart data for dataLayer
     */
    formatCart: function(cart) {
      if (!cart || !cart.items) return null;

      return {
        'currency': 'GBP',
        'value': parseFloat(cart.total_price || 0) / 100,
        'items': cart.items.map(item => this.formatProduct(item))
      };
    }
  };

  /**
   * Check if dataLayer feature is enabled
   */
  async function checkFeatureEnabled() {
    // Check localStorage for feature flag override, default to enabled
    // Note: Remote feature flag API is not yet deployed, so we skip the network call
    // to avoid 405 errors on every page load. Re-enable when API is ready.
    const stored = localStorage.getItem('feature_analytics_datalayer');
    return stored !== 'false'; // Default to enabled
  }

  // Initialize on DOM ready (with feature flag check)
  async function initializeDataLayer() {
    const enabled = await checkFeatureEnabled();
    if (enabled) {
      DataLayerManager.init();
    } else {
      debugLog('[DataLayer] Feature disabled via feature flag');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeDataLayer);
  } else {
    initializeDataLayer();
  }

  // Expose to window for global access
  window.KT = window.KT || {};
  window.KT.DataLayer = DataLayerManager;

})();
